import { useSignIn } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { FormInput } from '@/components/common/form-inputs/FormInput';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { toast } from '@/hooks/ui/use-toast';

const signInFormSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters' }),
});

const SignInForm = () => {
  const form = useForm<z.infer<typeof signInFormSchema>>({
    resolver: zodResolver(signInFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // state
  const [loading, setLoading] = useState<boolean>(false);

  // custom hooks
  const { signIn, isLoaded, setActive } = useSignIn();

  // event handlers
  const onSubmit = async (values: z.infer<typeof signInFormSchema>) => {
    if (!isLoaded) {
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'Please try again later',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await signIn.create({
        identifier: values.email,
        password: values.password,
      });

      if (response.status === 'complete') {
        toast({
          title: 'Sign In Success',
        });

        await setActive({
          session: response.createdSessionId,
          redirectUrl: `${window.location.origin}/dashboard`,
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Sign In Failed',
          description: 'Please check your credentials.',
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Sign In Failed',
        description: err.errors?.[0]?.message || 'An error occurred.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const email = import.meta.env.VITE_DEV_AUTO_LOGIN_EMAIL;
    const password = import.meta.env.VITE_DEV_AUTO_LOGIN_PASSWORD;
    if (!isLoaded || !signIn || !email || !password) return;
    setLoading(true);
    signIn
      .create({ identifier: email, password })
      .then(async (response) => {
        if (response.status === 'complete') {
          await setActive({
            session: response.createdSessionId,
            redirectUrl: `${window.location.origin}/dashboard`,
          });
        }
      })
      .catch(() => setLoading(false));
     
  }, [isLoaded]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-4">
        <div className="space-y-4">
          <FormInput
            control={form.control}
            name="email"
            type="email"
            label="Email"
            placeholder="Email"
          />
          <div className="relative">
            <FormInput
              control={form.control}
              name="password"
              type="password"
              label="Password"
              placeholder="Password"
            />
            <Link
              to={`/${window.localStorage.getItem('active-org-id')}/auth/forget-password`}
              className="as-body-03 absolute right-0 top-0 text-muted-foreground underline hover:text-black"
            >
              Forgot your password?
            </Link>
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? <Loader2 className="size-4 animate-spin" /> : 'Sign In'}
        </Button>
      </form>
    </Form>
  );
};

export { SignInForm };
